import React, { useContext, useEffect } from 'react'
import * as Sentry from '@sentry/gatsby'
import PropTypes from 'prop-types'
import { StyledSportsContainer } from './styles'
import storageService from '../../../services/storageService'
import { newSportsBookCulture } from '../../../utils/constants'
import { useFeatureIsOn, useFeatureValue } from '@growthbook/growthbook-react'
import { AuthContext } from '../../../context/authProvider'
import useSlugs from '../../../hooks/useSlugs'
import { navigate } from 'gatsby'
import { updateMobileLocation } from '../../../utils/mobileAppFunctionality'
import {
  getSportsbookToken,
  getSportsbookTokenV2,
} from '../../../adapters/user'
import { EnvConfig } from '../../../config/EnvConfig'
import { useDebounce } from '../../../hooks/useDebounce'
import useUpdateMobileAppToken from '../../../hooks/useUpdateMobileAppToken'
import { pushDataLayer } from '../../../utils/pushDataLayer'
import { isFbUser } from '../../../helpers/urlHelper'

const locale = process.env.GATSBY_INTL_LOCALE

const supportedQueryParams = [
  'page',
  'sportId',
  'categoryIds',
  'championshipIds',
  'eventId',
  'reservationCode',
  'couponType',
  'date',
  'horseRaceId',
  'virtualSportId',
  'shareCode',
  'maxOdd',
  'oddIds',
]

export const AltenarSportsbook = ({
  eventQueryParams,
  contextPage,
  setSeoPathParams,
}) => {
  const [loginSlug, sportsSlug, liveSportsSlug] = useSlugs([
    'login',
    'sports',
    'live-sports',
  ])

  const { getUserWallet } = useContext(AuthContext)
  const isSportsbookGoldenBoostEnabled = useFeatureValue(
    'sportsbook_golden_boost'
  )

  const getSearchParams = () => {
    if (eventQueryParams) {
      eventQueryParams = eventQueryParams?.replace('?', '')

      const searchParams = Object.fromEntries(
        new URLSearchParams(eventQueryParams).entries()
      )
      return {
        ...searchParams,
      }
    }

    const searchParams = Object.fromEntries(
      new URLSearchParams(document.location.search.substring(1)).entries()
    )
    return {
      ...searchParams,
    }
  }

  const getContentParams = () => {
    const {
      page = contextPage,
      categoryIds,
      championshipIds,
      sportId,
      periodType,
      couponType,
      eventId,
      reservationCode,
      date,
      horseRaceId,
      virtualSportId,
      shareCode,
      maxOdd,
      oddIds,
    } = getSearchParams()
    return {
      page: page,
      ...(eventId && {
        eventId: Number(eventId),
      }),
      ...(categoryIds && {
        categoryIds: categoryIds.split(',').map((n) => Number(n)),
      }),
      ...(championshipIds && {
        championshipIds: championshipIds.split(',').map((n) => Number(n)),
      }),
      ...(oddIds && {
        oddIds: oddIds.split(',').map((n) => Number(n)),
      }),
      ...(sportId && {
        sportId: Number(sportId),
      }),
      ...(periodType && {
        periodType: Number(periodType),
      }),
      ...(couponType && {
        couponType: Number(couponType),
      }),
      ...(reservationCode && {
        reservationCode: Number(reservationCode),
      }),
      ...(date && {
        date: Number(date),
      }),
      ...(horseRaceId && {
        horseRaceId: Number(horseRaceId),
      }),
      ...(virtualSportId && {
        virtualSportId: Number(virtualSportId),
      }),
      ...(shareCode && {
        shareCode,
      }),
      ...(maxOdd && {
        maxOdd: Number(maxOdd),
      }),
    }
  }

  const initializationSettingsV1 = async () => {
    const user = storageService.getUser()
    const culture = newSportsBookCulture?.[locale]?.goldenBoostDisabled

    if (!user) {
      return {
        token: '',
        culture,
      }
    }

    const { data } = await getSportsbookToken()
    return {
      token: data,
      culture,
    }
  }

  const initializationSettingsV2 = async () => {
    const user = storageService.getUser()

    if (!user) {
      return {
        token: '',
        culture: newSportsBookCulture?.[locale]?.goldenBoostDisabled,
      }
    }

    const { data } = await getSportsbookTokenV2()
    const { token: sbToken, showGoldenBoost = false } = data

    const culture = showGoldenBoost
      ? newSportsBookCulture?.[locale]?.goldenBoostEnabled
      : newSportsBookCulture?.[locale]?.goldenBoostDisabled

    return {
      token: sbToken,
      culture,
    }
  }

  const triggerScreenLoadedEvent = (data) => {
    pushDataLayer({
      eventName: 'screen-loaded',
      data: {
        screen_name: `sports-${contextPage}`,
        sport_id: data?.sportId,
        event_id: data?.eventId,
      },
    })
  }

  useEffect(() => {
    const data = getContentParams()
    triggerScreenLoadedEvent(data)
  }, [contextPage])

  const initializeSportsbook = async () => {
    try {
      const integration = isFbUser()
        ? process.env.GATSBY_ALTENAR_SKIN_FB_ID
        : process.env.GATSBY_ALTENAR_SKIN_ID
      const { token, culture } = isSportsbookGoldenBoostEnabled
        ? await initializationSettingsV2()
        : await initializationSettingsV1()

      window.altenarWSDK.init({
        integration,
        culture,
        token,
      })

      window.ASB = window.altenarWSDK.addSportsBook({
        props: {
          replace: true,
          routerType: 'memory',
          page: contextPage,
          onRouteChange: (data) => {
            if (data?.page) {
              let search = `?page=${data?.page}`
              if (data?.sportId) {
                search += `&sportId=${data?.sportId}`
              }
              const fakeLocation = {
                pathname: location.pathname,
                search: search,
              }
              updateMobileLocation(fakeLocation)
            }
          },
          onRouteChangeDetailed: (data) => {
            setSeoPathParams(data)
            Object.entries(data).forEach((item) => {
              const isValid = supportedQueryParams.find(
                (queryParam) => queryParam === item?.[0]
              )
              if (item?.[0] === 'event') {
                data.eventId = item?.[1]?.id
              }
              if (item?.[0] === 'sport') {
                data.sportId = item?.[1]?.id
              }
              if (!isValid) {
                delete data[item?.[0]]
              }
            })

            triggerScreenLoadedEvent(data)

            const updatedSearchParams = new URLSearchParams(data).toString()

            const sportsRoute = `/${sportsSlug}/`
            if (
              updatedSearchParams === 'page=overview' &&
              !location.pathname?.includes(sportsRoute)
            ) {
              navigate(sportsRoute)
            }

            const liveSportsRoute = `/${liveSportsSlug}/`
            if (
              updatedSearchParams === 'page=live' &&
              !location.pathname?.includes(liveSportsRoute)
            ) {
              navigate(liveSportsRoute)
            }

            const url = new URL(window.location.href)
            url.search = updatedSearchParams
            window.history.pushState(null, '', url)
          },
          onBetPlacement: (data) => {
            pushDataLayer({
              eventName: 'bet-placed',
              data: {
                bet_id: data,
              },
            })
            getUserWallet(true)
          },
          onSignInButtonClick: () => {
            navigate(`/${loginSlug}/`)
          },
          ...getContentParams(),
        },
        container: document.getElementById('sports-container'),
      })

      setSeoPathParams(getContentParams())

      window.altenarWSDK.set({
        token,
      })
    } catch (error) {
      Sentry.captureException('[InitializeSportsbookError]: ' + error)
    }
  }

  const renderSportsBook = async () => {
    const scriptExists = document.querySelector('#new-sportsbook')
    if (scriptExists) {
      await window?.ASB?.remove()
      scriptExists?.remove()
    }

    const script = document.createElement('script')
    script.src = EnvConfig.GATSBY_SPORTS_URL_V2
    script.id = 'new-sportsbook'

    script.onload = initializeSportsbook

    document.body.appendChild(script)
  }

  const debounceRenderSportsBook = useDebounce(renderSportsBook, 500)

  useEffect(() => {
    if (window) {
      try {
        debounceRenderSportsBook()
      } catch (error) {
        Sentry.captureException('[RenderSportsBookError]: ' + error)
      }
    }
  }, [isSportsbookGoldenBoostEnabled])

  useEffect(() => {
    // handle back navigation
    window?.addEventListener('popstate', () => {
      window?.altenarWSDK?.set({
        ...getContentParams(),
      })
    })

    return () => {
      window?.removeEventListener('popstate', null)
    }
  }, [])

  useUpdateMobileAppToken(debounceRenderSportsBook)

  const HIDE_ALTENAR_SPORTSBOOK = useFeatureIsOn(
    'fe_igp_hide_alternar_sportsbook'
  )

  if (HIDE_ALTENAR_SPORTSBOOK) {
    return null
  }

  return (
    <StyledSportsContainer>
      <div id="sports-container" />
    </StyledSportsContainer>
  )
}

AltenarSportsbook.propTypes = {
  eventQueryParams: PropTypes.string,
  contextPage: PropTypes.string,
  setSeoPathParams: PropTypes.func,
}
